import {
  Agent,
  Channel,
  CustomTheme,
  getMerchants,
  getMerchantStructureByMerchantId,
  getStructure,
  Merchant,
  PaymentMethodType,
  Role,
  User,
  UserType,
} from '@nemuru-eng/js-api-client';

import { ALL_APP_ROLES, APP_ROLES } from '@/config/config';
import { ApplicationType } from '@/config/types';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';
import { MerchantInStructure } from '@/types/MerchantInStructure';
import { queryStringBuilder } from '@/utils/queryStringBuilder';

export class UserEntity {
  public readonly user: User;
  public readonly merchant?: MerchantInStructure | undefined = undefined;
  public readonly channel?: Channel | undefined = undefined;
  public readonly agent?: Agent | undefined = undefined;
  public allowedProducts: ApplicationType[];
  public allowedPaymentMethodTypes: PaymentMethodType[];

  private readonly STRUCTURE_REQUIRED = [UserType.MERCHANT, UserType.CHANNEL, UserType.AGENT];

  constructor(user: User) {
    this.user = user;
    this.allowedProducts = this.getAllowedProductsByRole();
    this.allowedPaymentMethodTypes = this.getAllowedPaymentMethodsByUserType();
  }

  public isRoleSupported(): boolean {
    return ALL_APP_ROLES.includes(this.role);
  }

  public requiresStructure(): boolean {
    return this.STRUCTURE_REQUIRED.includes(this.user.userType);
  }

  public async revalidateStructure() {
    if (this.requiresStructure()) {
      return await getStructure();
    }
  }

  public hasRole(roles: Role[]): boolean {
    return this.user.roles.some((role) => roles.includes(role));
  }

  public isRoleAdminOrSuperAdmin(): boolean {
    return this.user.roles.some((role) => [Role.ROLE_ADMIN, Role.ROLE_SUPER_ADMIN].includes(role));
  }

  public isRoleMerchantChannelOrAgent(): boolean {
    return this.user.roles.some((role) => [Role.ROLE_MERCHANT, Role.ROLE_CHANNEL, Role.ROLE_AGENT].includes(role));
  }

  public isUserTypeAgent(): boolean {
    return this.isUserType(UserType.AGENT);
  }

  public isUserType(userType: UserType): boolean {
    return this.user.userType === userType;
  }

  public hasUserType(userTypes: UserType[]): boolean {
    return userTypes.includes(this.user.userType);
  }

  public get role(): Role {
    if (this.user.roles.length === 0) {
      throw Error('No roles assigned');
    }
    return this.user.roles[0];
  }

  public get name(): string {
    return this.user.username;
  }

  private getAllowedPaymentMethodsByUserType(): PaymentMethodType[] {
    const allPaymentMethodTypes = Object.values(PaymentMethodType).map((value) => value);
    if (this.isUserType(UserType.ADMIN) || this.isUserType(UserType.SUPER_ADMIN)) {
      return allPaymentMethodTypes;
    }
    return [];
  }

  private getAllowedProductsByRole(): ApplicationType[] {
    switch (this.role) {
      case APP_ROLES.LENDER:
        return [ApplicationType.CONSUMER_LOAN, ApplicationType.AUTO];
      case APP_ROLES.SUPER_ADMIN:
      case APP_ROLES.PARTNER:
      case APP_ROLES.MERCHANT_GROUP:
      case APP_ROLES.ADMIN:
      case APP_ROLES.CPC_BACK_OFFICE:
        return [...Object.values(ApplicationType)];
      default:
        return [];
    }
  }

  public async getMerchantGroupMerchants(): Promise<Merchant[] | undefined> {
    if (this.user.userType !== UserType.MERCHANT_GROUP) return;

    try {
      const merchantList = await getMerchants({
        query: queryStringBuilder({
          page: 1,
          pageSize: 1000,
          filters: undefined,
          orderBy: null,
          direction: null,
          search: undefined,
        }),
      });

      return merchantList.data;
    } catch (error) {
      console.error(error);
    }
  }

  public async getCustomTheme(): Promise<CustomTheme | undefined> {
    if (this.agent) {
      return this.agent.customTheme;
    }

    if (this.user.userType === UserType.MERCHANT_GROUP) {
      try {
        const merchants = await this.getMerchantGroupMerchants();

        if (!merchants || merchants.length === 0) return;

        const firstMerchantStructure = await getMerchantStructureByMerchantId({ merchantId: merchants[0].id });

        return new MerchantStructureEntity(firstMerchantStructure).getAgentOrFirstAgent().customTheme;
      } catch (error) {
        console.error(error);
      }
    }
  }

  public isCreditCardUser(): boolean {
    if (this.isUserType(UserType.ADMIN) || this.isUserType(UserType.SUPER_ADMIN)) {
      return false;
    }
    if (this.allowedPaymentMethodTypes?.includes(PaymentMethodType.CREDIT_CARD)) {
      return true;
    }
    return false;
  }
}
